import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  CalendarMinus,
  CalendarRange,
  Receipt,
  Database,
  Globe,
  FileBarChart2,
  Building2,
  ClipboardList,
} from 'lucide-react';
import { useTabPersistence } from '../../hooks/usePersistence';

import AdhocReports from './reports/AdhocReports';
import UnutilisedLeaveReport from './reports/UnutilisedLeaveReport';
import MonthlyLeaveUsageReport from './reports/MonthlyLeaveUsageReport';
import PayrollLeaveDeductionReport from './reports/PayrollLeaveDeductionReport';
import HQManpowerReport from './reports/HQManpowerReport';

const VALID_TABS = ['unutilised-leave', 'monthly-leave-usage', 'payroll-leave-deductions', 'project-labour-cost', 'project-manpower-deployment', 'labour-market-survey', 'hq-manpower', 'data-explorer'];

const Reports = () => {
  const [activeReportType, setActiveReportType] = useTabPersistence(
    'reports',
    'unutilised-leave',
    VALID_TABS
  );

  const reportTypes = [
    {
      id: 'unutilised-leave',
      label: 'Unutilised Leave',
      icon: CalendarMinus,
      component: UnutilisedLeaveReport
    },
    {
      id: 'monthly-leave-usage',
      label: 'Monthly Leave Usage',
      icon: CalendarRange,
      component: MonthlyLeaveUsageReport
    },
    {
      id: 'payroll-leave-deductions',
      label: 'Payroll Leave Deductions',
      icon: Receipt,
      component: PayrollLeaveDeductionReport
    },
    {
      id: 'project-labour-cost',
      label: 'Project Labour Cost',
      icon: Calculator,
      component: null,
      external: true,
      route: '/admin/reports/project-labour-cost',
      title: 'Project Labour Cost',
      description: 'Monthly labour cost review and validation workflow',
      cta: 'Start Workflow',
    },
    {
      id: 'project-manpower-deployment',
      label: 'Manpower Deployment',
      icon: Building2,
      component: null,
      external: true,
      route: '/admin/reports/project-manpower-deployment',
      title: 'Project Manpower Deployment',
      description: 'Who is deployed on which client project, month by month',
      cta: 'Open Report',
    },
    {
      id: 'labour-market-survey',
      label: 'Labour Market Survey',
      icon: ClipboardList,
      component: null,
      external: true,
      route: '/admin/reports/labour-market-survey',
      title: 'Labour Market Survey',
      description: 'Quarterly MOM return workspace — computed workforce tables, exception queues, and attestations',
      cta: 'Open Workspace',
    },
    {
      id: 'hq-manpower',
      label: 'HQ Manpower',
      icon: Globe,
      component: HQManpowerReport
    },
    {
      id: 'data-explorer',
      label: 'Data Explorer',
      icon: Database,
      component: AdhocReports
    },
  ];

  const activeReportTypeData = reportTypes.find(type => type.id === activeReportType);
  const ActiveComponent = activeReportTypeData?.component || UnutilisedLeaveReport;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FileBarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Reports</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Comprehensive HR reporting and analytics</p>
      </div>

      {/* Report Type Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {reportTypes.map((type) => {
          const Icon = type.icon;
          const isActive = activeReportType === type.id;
          
          return type.external ? (
            <Link
              key={type.id}
              to={`/admin/reports/${type.id}`}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {type.label}
            </Link>
          ) : (
            <button
              key={type.id}
              onClick={() => setActiveReportType(type.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {type.label}
            </button>
          );
        })}
      </div>

      {/* Active Report Component */}
      {activeReportTypeData?.external ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {(() => {
                const Icon = activeReportTypeData.icon || Calculator;
                return <Icon className="w-8 h-8 text-red-600" />;
              })()}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{activeReportTypeData.title || activeReportTypeData.label}</h3>
            <p className="text-gray-600 mb-6">{activeReportTypeData.description}</p>
            <Link
              to={activeReportTypeData.route}
              className="btn btn-admin-primary"
            >
              {(() => {
                const Icon = activeReportTypeData.icon || Calculator;
                return <Icon className="w-4 h-4 mr-2" />;
              })()}
              {activeReportTypeData.cta || 'Open'}
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border min-h-[600px] p-4">
          <ActiveComponent />
        </div>
      )}
    </div>
  );
};

export default Reports;
